<template>
	<transition name="slide-fade">
		<div v-if="lastUnlockedTitle" class="achievement-message">
			<span
				class="achievement-message__text achievement-message__text--head"
			>
				{{ translate("ui.achievement_unlocked") }}
				<span class="icon-check" />
			</span>
			<span
				class="achievement-message__text achievement-message__text--title"
				>{{ translate(lastUnlockedTitle) }}</span
			>
		</div>
	</transition>
</template>

<script lang="ts" setup>
import { AchievementTargetTypes } from "@/game/entities/config/achievements/achievement_target_types";
import useAchievementsStore from "@/store/useAchievementsStore";
import useResourcesStore from "@/store/useResourcesStore";
import useUserStore from "@/store/useUserStore";
import { translate } from "@/utils/translate";
import { storeToRefs } from "pinia";
import { onMounted, watch } from "vue";

const userStore = useUserStore();
const { tilesExplored, superClickersUsed, townsConquered, roadsAndBridges } =
	storeToRefs(userStore);

const achievementsStore = useAchievementsStore();
const { achievements, lastUnlockedTitle } = storeToRefs(achievementsStore);

const resourcesStore = useResourcesStore();
const {
	lumberUncappedIncome,
	stoneUncappedIncome,
	clayUncappedIncome,
	ironUncappedIncome,
	goodsUncappedIncome,
	steelUncappedIncome,
	populationIncome,
	armyUncappedIncome,
	adsWatchedTotal,
} = storeToRefs(resourcesStore);

onMounted((): void => {
	if (achievements.value.length > 0) {
		achievementsStore.setAchievementsState();
	}
});

watch(
	(): number => roadsAndBridges.value,
	(newValue: number): void => {
		if (newValue) {
			achievementsStore.setAchievementProgress(
				AchievementTargetTypes.HAS_ROADS_BUILD,
				newValue,
			);
		}
	},
);

watch(
	(): number => townsConquered.value,
	(newValue: number): void => {
		if (newValue) {
			achievementsStore.setAchievementProgress(
				AchievementTargetTypes.SETTLEMENT_CONTROL,
				newValue,
			);
		}
	},
);

watch(
	(): number => superClickersUsed.value,
	(newValue: number): void => {
		if (newValue) {
			achievementsStore.setAchievementProgress(
				AchievementTargetTypes.HAS_CLICKERS,
				newValue,
			);
		}
	},
);

watch(
	(): number => adsWatchedTotal.value,
	(newValue: number): void => {
		if (newValue) {
			achievementsStore.setAchievementProgress(
				AchievementTargetTypes.AD_WATCH,
				newValue,
			);
		}
	},
);

watch(
	(): number => lumberUncappedIncome.value,
	(newValue: number): void => {
		if (newValue) {
			achievementsStore.setAchievementProgress(
				AchievementTargetTypes.HAS_LUMBER_INCOME,
				newValue,
			);
		}
	},
);

watch(
	(): number => stoneUncappedIncome.value,
	(newValue: number): void => {
		if (newValue) {
			achievementsStore.setAchievementProgress(
				AchievementTargetTypes.HAS_STONE_INCOME,
				newValue,
			);
		}
	},
);

watch(
	(): number => clayUncappedIncome.value,
	(newValue: number): void => {
		if (newValue) {
			achievementsStore.setAchievementProgress(
				AchievementTargetTypes.HAS_CLAY_INCOME,
				newValue,
			);
		}
	},
);

watch(
	(): number => ironUncappedIncome.value,
	(newValue: number): void => {
		if (newValue) {
			achievementsStore.setAchievementProgress(
				AchievementTargetTypes.HAS_IRON_INCOME,
				newValue,
			);
		}
	},
);

watch(
	(): number => goodsUncappedIncome.value,
	(newValue: number): void => {
		if (newValue) {
			achievementsStore.setAchievementProgress(
				AchievementTargetTypes.HAS_GOODS_INCOME,
				newValue,
			);
		}
	},
);

watch(
	(): number => steelUncappedIncome.value,
	(newValue: number): void => {
		if (newValue) {
			achievementsStore.setAchievementProgress(
				AchievementTargetTypes.HAS_STEEL_INCOME,
				newValue,
			);
		}
	},
);

watch(
	(): number => populationIncome.value,
	(newValue: number): void => {
		if (newValue) {
			achievementsStore.setAchievementProgress(
				AchievementTargetTypes.HAS_WORKFORCE,
				newValue,
			);
		}
	},
);

watch(
	(): number => armyUncappedIncome.value,
	(newValue: number): void => {
		if (newValue) {
			achievementsStore.setAchievementProgress(
				AchievementTargetTypes.HAS_ARMY,
				newValue,
			);
		}
	},
);

watch(
	(): number => tilesExplored.value,
	(newValue: number): void => {
		if (newValue) {
			achievementsStore.setAchievementProgress(
				AchievementTargetTypes.EXPLORE_TILES,
				newValue,
			);
		}
	},
);

watch(
	(): string => lastUnlockedTitle.value,
	(newValue: string): void => {
		if (newValue) {
			setTimeout(() => {
				achievementsStore.setLastUnlockedTitle("");
			}, 3000);
		}
	},
);
</script>

<style lang="scss" scoped>
.achievement-message {
	position: relative;
	@include background(
		url($path-images + "backgrounds/achievements/achievement-notice.avif"),
		auto,
		0 0,
		no-repeat
	);
	left: -0.5rem;
	margin-bottom: 0.5rem;
	background-size: contain;
	width: 12.25rem;
	height: 3.6rem;
	@include flex-col-center;
	&__text {
		font-size: 0.688rem;
		text-shadow: 0.044rem 0.044rem 0.063rem rgba(0, 0, 0, 0.6);
		&--head {
			color: #19b345;
			text-transform: uppercase;
		}
		&--title {
			margin-bottom: 0.25rem;
			color: #fff3dd;
		}
		:deep(.icon-check) {
			display: inline-block;
			margin-top: 0.25rem;
			margin-left: 0.25rem;
			width: 0.813rem;
			height: 0.813rem;
		}
	}
}
.slide-fade-leave-active,
.slide-fade-enter-active {
	transition: all 0.5s ease-out;
}

.slide-fade-enter-from,
.slide-fade-leave-to {
	transform: translateX(-3rem);
	opacity: 0;
}
</style>
